import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import useMembershipValidation from 'Shared/Hooks/useMembershipValidation'
import Alert from './MembershipAlert'
import { Button, Input, InputGroup, InputGroupText, Label } from 'reactstrap'
import ForgotMembershipModal from './ForgoMembershipModal/ForgotMembershipModal'
import FontAwesomeIcon from 'Shared/FontAwesomeIcon/FontAwesomeIcon'
import FormGroupField from 'Shared/Formik/FormGroupField'
import Link from 'Shared/Link/Link'
import Loading from 'Shared/Loading'
import 'Shared/Coupon/Coupon.scss' // Reuse floating button to clear the input

const Membership = ({ dates, email, onApply, onClear, onlyInput = false, renewMembershipUrl: url,
                      isDailyAccommodation, isExclusive, savedCode, slug, wrapperClasses }) => {
  const [clearCode, setClearCode] = useState(false)
  const [forgotMembershipModalOpen, setForgotMembershipModalOpen] = useState(false)
  const [applyClicked, setApplyClicked] = useState(false)
  const [localValue, setLocalValue] = useState(savedCode)
  const { validateMembership, data, loading, error: validationError } = useMembershipValidation()
  const validatedOnLoad = useRef(false)
  const [membershipApplied, setMembershipApplied] = useState(false)
  const applyButtonClasses = 'd-inline-flex align-items-center justify-content-center'
  const clearIcon = membershipApplied ? 'check' : 'xmark'
  const buttonColor = membershipApplied ? 'success' : 'danger'
  const hasDiscountError = validationError?.message === 'NO_APLICABLE_DISCOUNT'

  const toggleForgotMembershipModal = value => {
    setForgotMembershipModalOpen(prevState => (typeof value === 'boolean' ? value : !prevState))
  }

  const getButtonContent = () => {
    if (loading) return <Loading size="1x" />

    return clearCode ? 'Applied' : 'Apply'
  }

  const validateMembershipCode = code => {
    const variables = { slug, code, email, dates, isDailyAccommodation, isExclusive }
    validateMembership(variables)
  }

  useEffect(() => {
    onClear()
  }, [])

  useEffect(() => {
    if (data && !loading && !validationError) {
      const { isValid } = data?.campground?.validateMembership || {}
      setApplyClicked(true)
      setClearCode(true)
      setMembershipApplied(isValid)
      isValid ? onApply(localValue) : onClear()
    }

    if (validationError) {
      setMembershipApplied(false)
      setApplyClicked(true)
      setClearCode(false)
    }
  }, [data, loading, validationError])

  useEffect(() => {
    const code = localValue?.trim()
    if (code?.length && email?.length && !validatedOnLoad.current) {
      validateMembershipCode(code)
      validatedOnLoad.current = true
    }
  }, [email])


  return (
    <div className={`membership-wrapper row mb-4 pb-4 ${wrapperClasses}`}>
      {!onlyInput && (
        <>
          <ForgotMembershipModal isOpen={forgotMembershipModalOpen} slug={slug}
                                 toggle={toggleForgotMembershipModal} />

          <div className="d-flex align-items-center">
            <h4 className="mb-0">Membership</h4>

            <Button color="link" onClick={toggleForgotMembershipModal}>
              Forgot your membership?
            </Button>
          </div>

          <p className="text-muted mt-2 mb-3">
            A valid membership is required to complete your reservation. Don't have one?

            <Link className="ms-2 text-salmon" isExternal target="_blank" to={url}>
              Click here.
            </Link>
          </p>
        </>
      )}

      <FormGroupField className="membership mb-0" name="membershipCode" showErrors={false}>
        {({ form, meta: { error, touched } }) => {
          const getErrorMessage = () => {
            if (membershipApplied) return
            if (hasDiscountError)
              return "Sorry! It seems your membership type isn't eligible for this price"
            if (error && (!applyClicked || !touched))
              return 'Click the Apply button to validate your code.'

            return error // 'This field is required'
          }

          const onApplyCode = () => {
            if (localValue.trim().length > 0) {
              form.setFieldValue('membershipCode', localValue.trim())
              setApplyClicked(true)
              setClearCode(true)
              validateMembershipCode(localValue.trim())
            }
          }

          const onChange = ({ target }) => {
            if (!clearCode) setLocalValue(target.value)
          }

          const onClearCode = () => {
            setApplyClicked(false)
            setClearCode(false)
            setLocalValue('')
            setMembershipApplied(false)
            form.setFieldValue('membershipCode', '')
            onClear()
          }

          useEffect(() => {
            form.setFieldValue('membershipCode', savedCode?.trim())
          }, [savedCode])

          return (
            <>
              <Label for="membershipCode">Membership Code</Label>

              <InputGroup className="floating-label-height">
                <Input className="text-gray-600 border-end-0" id="membershipCode" name="membershipCode"
                       onChange={onChange} placeholder="Enter Membership code" type="text" value={localValue} />

                {applyClicked && !loading && (
                  <InputGroupText className="bg-white px-3">
                    <Button aria-label="Clear" className="clear-button rounded-pill p-0" color={buttonColor}
                            onClick={onClearCode} outline size="sm">
                      <FontAwesomeIcon icon={clearIcon} />
                    </Button>
                  </InputGroupText>
                )}

                <Button color="blue-dark" disabled={clearCode || loading} onClick={onApplyCode}>
                  <span className={applyButtonClasses} style={{ minWidth: 70 }}>
                    {getButtonContent()}
                  </span>
                </Button>
              </InputGroup>

              {(applyClicked || (error && touched)) && !loading && (
                <Alert error={getErrorMessage()} success={membershipApplied} url={url} />
              )}
            </>
          )
        }}
      </FormGroupField>
    </div>
  )
}

Membership.defaultProps = {
  wrapperClasses: '',
  onlyInput: false,
  isDailyAccommodation: false,
  isExclusive: false,
  savedCode: '',
}

Membership.propTypes = {
  dates: PropTypes.arrayOf(PropTypes.shape({
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
  })).isRequired,
  email: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onlyInput: PropTypes.bool,
  renewMembershipUrl: PropTypes.string.isRequired,
  isExclusive: PropTypes.bool,
  isDailyAccommodation: PropTypes.bool,
  savedCode: PropTypes.string,
  slug: PropTypes.string.isRequired,
  wrapperClasses: PropTypes.string,
}

export default Membership
